import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * AI Generation Context
 * 
 * Provides global tracking of AI generation state across the application.
 * Used to prevent navigation while AI content is being generated.
 */

const AIGenerationContext = createContext(null);

export const useAIGeneration = () => {
  const context = useContext(AIGenerationContext);
  if (!context) {
    throw new Error('useAIGeneration must be used within an AIGenerationProvider');
  }
  return context;
};

export const AIGenerationProvider = ({ children }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationSource, setGenerationSource] = useState(null); // 'content-builder' | 'course-builder'
  const [generationType, setGenerationType] = useState(null); // 'single' | 'multiple' | 'all-topic' | 'all-course'
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [showPreGenerationWarning, setShowPreGenerationWarning] = useState(false);
  const [pendingGenerationCallback, setPendingGenerationCallback] = useState(null);
  const [itemCount, setItemCount] = useState(0);
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [unsavedChangesSource, setUnsavedChangesSource] = useState(null); // 'content-builder' | 'course-builder'
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
  const pendingNavigateRef = useRef(null);

  // Register unsaved changes from a component
  const registerUnsavedChanges = useCallback((source, hasChanges) => {
    console.log('📝 registerUnsavedChanges - source:', source, 'hasChanges:', hasChanges);
    setHasUnsavedChanges(hasChanges);
    setUnsavedChangesSource(hasChanges ? source : null);
  }, []);

  // Clear unsaved changes (called when changes are saved or discarded)
  const clearUnsavedChanges = useCallback(() => {
    console.log('🧹 clearUnsavedChanges');
    setHasUnsavedChanges(false);
    setUnsavedChangesSource(null);
  }, []);

  // Start tracking AI generation. `background=true` skips the
  // navigation-lock behaviour — the job runs in the worker (or as
  // backgrounded asyncio.create_task) and the user is free to navigate.
  // The navbar Jobs widget surfaces progress + completion toasts, so
  // the heavyweight modal lock is no longer needed for these flows.
  const startGeneration = useCallback((source, type = 'single', count = 1, options = {}) => {
    const background = !!options.background;
    console.log('🚀 startGeneration called - source:', source, 'type:', type, 'count:', count, 'background:', background);
    if (background) {
      // Track source/type for diagnostics but DON'T flip isGenerating —
      // that's what the navigation-block dialog watches.
      setGenerationSource(source);
      setGenerationType(type);
      setItemCount(count);
      return;
    }
    console.log('🚀 Setting isGenerating to TRUE');
    setIsGenerating(true);
    setGenerationSource(source);
    setGenerationType(type);
    setItemCount(count);
  }, []);

  // Stop tracking AI generation
  const stopGeneration = useCallback(() => {
    console.log('🛑 stopGeneration called - Setting isGenerating to FALSE');
    setIsGenerating(false);
    setGenerationSource(null);
    setGenerationType(null);
    setItemCount(0);
  }, []);

  // Show pre-generation warning for multi-item generation
  const showMultiItemWarning = useCallback((count, onConfirm) => {
    setItemCount(count);
    setPendingGenerationCallback(() => onConfirm);
    setShowPreGenerationWarning(true);
  }, []);

  // Handle confirm multi-item generation
  const handleConfirmGeneration = useCallback(() => {
    setShowPreGenerationWarning(false);
    if (pendingGenerationCallback) {
      pendingGenerationCallback();
      setPendingGenerationCallback(null);
    }
  }, [pendingGenerationCallback]);

  // Handle cancel multi-item generation
  const handleCancelGeneration = useCallback(() => {
    setShowPreGenerationWarning(false);
    setPendingGenerationCallback(null);
    setItemCount(0);
  }, []);

  // Check if navigation should be blocked - block during generation or if unsaved changes
  const checkNavigation = useCallback((navigateCallback) => {
    if (isGenerating) {
      setShowNavigationWarning(true);
      return false;
    }
    if (hasUnsavedChanges) {
      setShowUnsavedChangesWarning(true);
      return false;
    }
    return true;
  }, [isGenerating, hasUnsavedChanges]);

  // Safe navigate - blocks navigation during generation or if unsaved changes
  const safeNavigate = useCallback((navigate, path, options) => {
    console.log('🔒 safeNavigate called - isGenerating:', isGenerating, 'hasUnsavedChanges:', hasUnsavedChanges, 'path:', path);
    if (isGenerating) {
      console.log('🚫 Navigation blocked - AI generation in progress');
      setShowNavigationWarning(true);
      return false;
    }
    if (hasUnsavedChanges) {
      console.log('🚫 Navigation blocked - unsaved changes');
      setPendingNavigationPath(path);
      pendingNavigateRef.current = (p) => navigate(p, options);
      setShowUnsavedChangesWarning(true);
      return false;
    }
    console.log('✅ Navigation allowed');
    navigate(path, options);
    return true;
  }, [isGenerating, hasUnsavedChanges]);

  // Handle discard unsaved changes and navigate
  const handleDiscardAndNavigate = useCallback(() => {
    console.log('🗑️ Discarding unsaved changes and navigating');
    setShowUnsavedChangesWarning(false);
    setHasUnsavedChanges(false);
    setUnsavedChangesSource(null);
    if (pendingNavigateRef.current && pendingNavigationPath) {
      pendingNavigateRef.current(pendingNavigationPath);
    }
    setPendingNavigationPath(null);
    pendingNavigateRef.current = null;
  }, [pendingNavigationPath]);

  // Handle cancel navigation (stay on page)
  const handleCancelUnsavedNavigation = useCallback(() => {
    console.log('↩️ Cancelled navigation - staying on page');
    setShowUnsavedChangesWarning(false);
    setPendingNavigationPath(null);
    pendingNavigateRef.current = null;
  }, []);

  // Close navigation warning (just hides dialog, doesn't navigate)
  const handleCloseNavigationWarning = useCallback(() => {
    setShowNavigationWarning(false);
  }, []);

  const value = {
    isGenerating,
    generationSource,
    generationType,
    startGeneration,
    stopGeneration,
    checkNavigation,
    safeNavigate,
    showNavigationWarning,
    showMultiItemWarning,
    // Unsaved changes
    hasUnsavedChanges,
    unsavedChangesSource,
    registerUnsavedChanges,
    clearUnsavedChanges,
  };

  return (
    <AIGenerationContext.Provider value={value}>
      {children}
      
      {/* Pre-Generation Warning Dialog for Multi-Item Generation */}
      <Dialog open={showPreGenerationWarning} onOpenChange={setShowPreGenerationWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Generate Content for {itemCount} Items?
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              You are about to generate AI content for <strong>{itemCount} items</strong>. 
              <br /><br />
              <strong className="text-orange-600">Note:</strong> You can stop the process at any time — generation will complete for the current content block and then stop. Content already generated will be saved.
              <br /><br />
              This may take several minutes depending on the number of items.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelGeneration}>
              Cancel
            </Button>
            <Button onClick={handleConfirmGeneration}>
              Start Generation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Navigation Blocked Dialog During Generation/Execution */}
      <Dialog open={showNavigationWarning} onOpenChange={handleCloseNavigationWarning}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              {generationType === 'lab-execution' ? 'Lab Execution in Progress' : 'AI Generation in Progress'}
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              {generationType === 'lab-execution' ? (
                <>
                  {generationSource === 'epm-lab' ? 'Your EPM workflow' : 'Your AI Lab'} is currently executing.
                  <br /><br />
                  <strong>To stop and navigate away:</strong>
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    <li>Click <strong>&quot;OK, I&apos;ll Go Back&quot;</strong> to return to the lab</li>
                    <li>Click the <strong>Stop</strong> button to abort execution</li>
                    <li>Wait for the lab to gracefully stop</li>
                    <li>Then you can navigate elsewhere</li>
                  </ol>
                  <br />
                  <span className="text-sm text-gray-500">The Stop button will gracefully abort the execution after completing the current step.</span>
                </>
              ) : generationType === 'ai-course-generation' ? (
                <>
                  AI is generating your course structure.
                  <br /><br />
                  <strong>To stop and navigate away:</strong>
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    <li>Click <strong>&quot;OK, I&apos;ll Go Back&quot;</strong> to return</li>
                    <li>Click the <strong>Stop Generation</strong> button</li>
                    <li>Then you can navigate elsewhere</li>
                  </ol>
                  <br />
                  <span className="text-sm text-gray-500">Stopping will cancel the API request and you can try generating again later.</span>
                </>
              ) : (
                <>
                  Content generation is in progress.
                  <br /><br />
                  <strong>To stop and navigate away:</strong>
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    <li>Click <strong>&quot;OK, I&apos;ll Go Back&quot;</strong> to return</li>
                    <li>Click the <strong>Stop</strong> button in the progress bar</li>
                    <li>Wait for the current item to complete</li>
                    <li>Then you can navigate elsewhere</li>
                  </ol>
                  {generationType === 'all-course' && (
                    <>
                      <br />
                      <span className="text-sm text-gray-500">Generating content for the entire course may take several minutes.</span>
                    </>
                  )}
                  {generationType === 'all-topic' && (
                    <>
                      <br />
                      <span className="text-sm text-gray-500">Generating content for all blocks in this topic...</span>
                    </>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleCloseNavigationWarning}>
              OK, I&apos;ll Go Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Unsaved Changes Warning Dialog */}
      <Dialog open={showUnsavedChangesWarning} onOpenChange={handleCancelUnsavedNavigation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              You have unsaved changes in {unsavedChangesSource === 'content-builder' ? 'the Course Blueprint' : unsavedChangesSource === 'course-builder' ? 'the Course Builder' : 'this page'}.
              <br /><br />
              If you leave now, your changes will be lost. Would you like to save your changes before leaving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelUnsavedNavigation}>
              Stay on Page
            </Button>
            <Button variant="destructive" onClick={handleDiscardAndNavigate}>
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AIGenerationContext.Provider>
  );
};

export default AIGenerationContext;
